import { useQuery } from '@tanstack/react-query'
import fp from 'lodash/fp'
import {
  useSearchId,
  PRODUCT_SEARCH_INDEX_KEY,
} from 'shared/hooks/useSearchId/useSearchId'
import { getSegmentAnonymousId } from 'lib/getSegmentAnonymousId/getSegmentAnonymousId'
import { constructFilterQueryString } from '../lib'
import {
  fetchBrand,
  fetchBrandProducts,
  fetchBrandsSearch,
  fetchCatalogCategoryFacets,
  fetchCatalogCategoryProducts,
  fetchCuratedProductCollections,
  fetchProductsSearch,
} from '.'

const BRANDS_SEARCH_STATE_TIME = 600000
const BRAND_PAGE_STALE_TIME = 600000
const CATALOG_CATEGORY_STALE_TIME = 600000 // 10 minutes
const CURATED_PRODUCT_COLLECTIONS_STALE_TIME = 600000 // 10 minutes
const PRODUCTS_SEARCH_STALE_TIME = 600000 // 10 minutes

/**
 * Utility function that derives the order by direction params for lodash's `orderBy` function.
 *
 * @param {string[]} args The iteratees to sort by.
 * @param {'asc' | 'desc'} direction The sort orders of iteratees.
 * @returns {Array<'asc' | 'desc'>} An array of sort directions if `args` is an array. Otherwise returns a string.
 */
function deriveOrderDirectionArgs(args, direction) {
  return Array.isArray(args)
    ? new Array(args.length).fill(direction)
    : direction
}

/**
 * @typedef {object} OrderConfig
 * @property {function} asc - Will return the provided collection in ascending order.
 * @property {function} desc - Will return the provided collection in descending order.
 */

/**
 * Small utility meant for building up deterministic sorting/ordering
 * functions.
 *
 * @param {function|string|array} order Iteratees passed to lodash orderBy https://lodash.com/docs/4.17.15#orderBy.
 * @returns {OrderConfig}
 */
const makeOrderBy = (order) => ({
  desc: fp.orderBy(order, deriveOrderDirectionArgs(order, 'desc')),
  asc: fp.orderBy(order, deriveOrderDirectionArgs(order, 'asc')),
})

/**
 * @typedef {object} SortProductsBy
 * @proprty {OrderConfig} price
 * @proprty {OrderConfig} newest
 * @proprty {OrderConfig} rating
 * @proprty {OrderConfig} reviews
 * @proprty {null} popular Noop odering assuming server has ordered by popularity.
 */
const sortProductsBy = {
  price: makeOrderBy('pricing.current'),
  newest: makeOrderBy((product) => new Date(product.createdAt)),
  rating: makeOrderBy(['rating', 'reviewCount']),
  reviews: makeOrderBy('reviewCount'),
  // noop since popular is the default sorting (assuming the server sorts by popular)
  popular: null,
}

/**
 * @typedef {object} FilterProductsBy Key value of functional conditions.
 *    If the first function of the condition pair evaluates to true, the 2nd function
 *    will be returned and can be used in a lodash compose call.
 *    https://codingwithjs.rocks/blog/better-branching-with-lodash-cond
 * @property {function} availability Will filter by availability if filter is set to true.
 */
const filterProductsBy = {
  available: fp.cond([
    [
      (filters) => filters.available === true,
      () => fp.filter((product) => product.isAvailableOnBabylist === true),
    ],
  ]),
  brand: fp.cond([
    [
      (filters) => filters.brands?.length > 0,
      (filters) =>
        fp.filter(
          (product) =>
            filters.brands
              .map((brand) => brand.toLowerCase())
              .indexOf(product.brand.toLowerCase()) !== -1
        ),
    ],
  ]),
  category: fp.cond([
    [
      (filters) => filters.categories?.length > 0,
      (filters) =>
        fp.filter(
          (product) =>
            filters.categories
              .map((category) => category.toLowerCase())
              .indexOf(product.category?.toLowerCase()) !== -1
        ),
    ],
  ]),
  minRating: fp.cond([
    [
      (filters) => !!filters.minRating,
      (filters) =>
        fp.filter(
          (product) => product.rating && product.rating >= filters.minRating
        ),
    ],
  ]),
  minPrice: fp.cond([
    [
      (filters) => !!filters.minPrice,
      (filters) =>
        fp.filter((product) => product.pricing?.max >= filters.minPrice),
    ],
  ]),
  maxPrice: fp.cond([
    [
      (filters) => !!filters.maxPrice,
      (filters) =>
        fp.filter((product) => product.pricing?.min <= filters.maxPrice),
    ],
  ]),
  onSale: fp.cond([
    [
      (filters) => filters.onSale === true,
      () => fp.filter((product) => product.onSale === true),
    ],
  ]),
}

/**
 * @typedef {object} OrderByOptions
 * @property {('popular'|'price'|'rating'|'reviews'|'newest')} key The sort key used for odering.
 * @property {('asc'|'desc')} direction Direction for the ordering operation.
 *
 * @typedef {object} SelectProductsOptions
 * @property {object} filters Any known filters and their configuration. Configuration isn't standardized.
 * @property {OrderByOptions} orderBy Configuration for odering operation.
 */

/**
 * Various sort and filter operations to perform on fetched hero products.
 *
 * @param {array} products Array of HeroProduct objects.
 * @param {SelectProductsOptions} options
 * @returns {array} HeroProduct objects after being filtered and ordered.
 */
export const selectProducts = (
  products = [],
  { filters = {}, orderBy = { key: 'popular', direction: 'asc' } } = {}
) =>
  fp.compose(
    [
      // filtering
      filterProductsBy.available(filters),
      filterProductsBy.brand(filters),
      filterProductsBy.category(filters),
      filterProductsBy.minRating(filters),
      filterProductsBy.minPrice(filters),
      filterProductsBy.maxPrice(filters),
      filterProductsBy.onSale(filters),
      // sorting
      sortProductsBy[orderBy.key]?.[orderBy.direction ?? 'asc'],
      // if any of the filters or sorting options evaluate to falsey '
      // we don't compose them (opt out of running that filter or ordering)
    ].filter(Boolean)
  )(products)

/**
 * Helper that is used for building a shared react-query key. Mostly needed for consistency
 * when hydrating data from server rendering.
 *
 * @param {number|string} catalogCategoryId - Id or slug of a catalog category (leaf category).
 * @param {object} params - Query params for API request.
 */
export const catalogCategoryProductsKey = (catalogCategoryId, params) => [
  'catalogCategoryProducts',
  catalogCategoryId,
  params,
]

/**
 * Hook that will fetch catalog category products.
 *
 * @param {number|string} catalogCategoryId Id or slug of a catalog category (leaf category).
 * @param {object} params - Query params fro API request.
 * @returns {object} All the things useQuery returns.
 */
export const useCatalogCategoryProducts = (catalogCategoryId, params = {}) => {
  const requestParams = params.inFacetsFeature
    ? {
        filters: constructFilterQueryString(params.filters),
        inFacetsFeature: params.inFacetsFeature,
        sort: params.sort,
      }
    : {
        heroes: params.heroes,
        limit: params.limit,
        on_sale: params.filters?.onSale,
        min_price: params.filters?.minPrice,
        max_price: params.filters?.maxPrice,
        inFacetsFeature: params.inFacetsFeature,
      }
  const queryKey = catalogCategoryProductsKey(catalogCategoryId, requestParams)

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: () =>
      fetchCatalogCategoryProducts(queryKey, catalogCategoryId, requestParams),
    // somewhat realistic caching time so navigating around categories should feel snappy
    staleTime: CATALOG_CATEGORY_STALE_TIME,
  })

  // If we don't have `data` we assume fetching is happening or there is an error.
  // Let the caller handle.
  if (!query.data) return query

  // Handle sorting/filtering within hook in case we ever want to
  // delegate filtering/ordering to the server (ex. paginated responses).
  const selectedData = selectProducts(
    query.data.heroProducts.map((hp) => ({ ...hp, rating: hp.rating ?? 0 })),
    {
      filters: params.filters,
      orderBy: params.orderBy,
    }
  )

  return {
    ...query,
    data: { heroProducts: selectedData, facets: query.data.facets },
  }
}

useCatalogCategoryProducts.prefetch = async (
  queryClient,
  catalogCategoryId,
  params
) => {
  queryClient.prefetchQuery(
    catalogCategoryProductsKey(catalogCategoryId, params),
    () =>
      fetchCatalogCategoryProducts(
        catalogCategoryProductsKey(catalogCategoryId, params),
        catalogCategoryId,
        params
      )
  )
}

export const catalogCategoryFacetsKey = (catalogCategoryId, params) => [
  'catalogCategoryFacets',
  catalogCategoryId,
  params,
]

/**
 * Hook that will fetch catalog category facets.
 *
 * @param {number|string} catalogCategoryId Id or slug of a catalog category (leaf category).
 * @param {object} filters - Query params for API request.
 * @returns {object} All the things useQuery returns.
 */
export const useCatalogCategoryFacets = (catalogCategoryId, filters) => {
  const queryKey = catalogCategoryFacetsKey(catalogCategoryId, filters)

  const query = useQuery({
    queryKey,
    queryFn: () =>
      fetchCatalogCategoryFacets(queryKey, catalogCategoryId, filters),
    staleTime: CATALOG_CATEGORY_STALE_TIME,
    keepPreviousData: true,
  })

  if (!query.data) return query

  return {
    ...query,
    data: { facets: query.data.facets },
  }
}

/**
 * Hook that will fetch curated product collections for the store landing page.
 *
 * @returns {object} All the things useQuery returns.
 */
export const useCuratedProductCollections = () => {
  const query = useQuery({
    queryKey: ['curatedProductCollections'],
    queryFn: fetchCuratedProductCollections,
    staleTime: CURATED_PRODUCT_COLLECTIONS_STALE_TIME,
  })

  return {
    ...query,
    data: query.data,
  }
}

/**
 * Hook that will fetch brand.
 *
 * @param {number} brandId id of a brand.
 * @returns {object} All the things useQuery returns.
 */
export const useBrand = (brandId) => {
  const query = useQuery({
    queryKey: ['brand', brandId],
    queryFn: () => fetchBrand(['brand', brandId], brandId),
    staleTime: BRAND_PAGE_STALE_TIME,
  })

  return {
    ...query,
    data: query.data,
  }
}

/**
 * Helper that is used for building a shared react-query key.
 *
 * @param {number} brandId - Id of a brand.
 * @param {object} params - Query params for API request.
 */
export const brandProductsKey = (brandId, params) => [
  'brandProducts',
  brandId,
  params,
]

/**
 * Hook that will fetch brand products.
 *
 * @param {number} brandId id of a brand.
 * @param {object} params - Query params fro API request.
 * @returns {object} All the things useQuery returns.
 */
export const useBrandProducts = (brandId, params) => {
  const requestParams = (({ limit }) => ({ limit }))(params)

  const query = useQuery({
    queryKey: brandProductsKey(brandId, requestParams),
    queryFn: () =>
      fetchBrandProducts(
        brandProductsKey(brandId, requestParams),
        brandId,
        requestParams
      ),
    staleTime: BRAND_PAGE_STALE_TIME,
  })

  // If we don't have `data` we assume fetching is happening or there is an error.
  // Let the caller handle.
  if (!query.data) return query

  // Handle sorting/filtering within hook in case we ever want to
  // delegate filtering/ordering to the server (ex. paginated responses).
  const selectedData = selectProducts(query.data.heroProducts, {
    filters: params.filters,
    orderBy: params.orderBy,
  })

  return {
    ...query,
    data: { heroProducts: selectedData },
  }
}

/**
 * Helper that is used for building a shared react-query key.
 *
 * @param {string} searchTerm - search query string.
 */
export const brandsSearchKey = (searchTerm) => ['brandsSearch', searchTerm]

/**
 * Hook that will find a brand given a searchTerm.
 *
 * @param {string} searchTerm The search term.
 * @returns {object} All the things useQuery returns.
 */
export const useBrandsSearch = (searchTerm) => {
  const query = useQuery({
    queryKey: brandsSearchKey(searchTerm),
    queryFn: () => fetchBrandsSearch(brandsSearchKey(searchTerm), searchTerm),
    staleTime: BRANDS_SEARCH_STATE_TIME,
  })

  if (!query.data) return query

  return {
    ...query,
    data: query.data,
  }
}

/**
 * Helper that is used for building a shared react-query key.
 *
 * @param {string} searchTerm - search query string.
 * @param {object} params - Query params fro API request.
 */
export const productsSearchKey = (searchTerm, params) => [
  'productsSearch',
  searchTerm,
  params,
]

/**
 * Hook that will fetch products given a searchTerm.
 *
 * @param {string} searchTerm The search term.
 * @param {object} params - Query params fro API request.
 * @returns {object} All the things useQuery returns.
 */
export const useProductsSearch = (searchTerm, params) => {
  const segmentAnonymousId = getSegmentAnonymousId()
  const requestParams = (({ heroes, limit }) => ({
    heroes,
    count: limit,
    segment_anonymous_id: segmentAnonymousId,
  }))(params)

  const [setSearchId] = useSearchId(PRODUCT_SEARCH_INDEX_KEY)

  const query = useQuery({
    queryKey: productsSearchKey(searchTerm, requestParams),
    queryFn: () =>
      fetchProductsSearch(
        productsSearchKey(searchTerm, requestParams),
        searchTerm,
        requestParams
      ),
    // somewhat realistic caching time so navigating around categories should feel snappy
    staleTime: PRODUCTS_SEARCH_STALE_TIME,
  })

  // If we don't have `data` we assume fetching is happening or there is an error.
  // Let the caller handle.
  if (!query.data) return query

  // Handle sorting/filtering within hook in case we ever want to
  // delegate filtering/ordering to the server (ex. paginated responses).
  const selectedData = selectProducts(query.data.heroProducts, {
    filters: params.filters,
    orderBy: params.orderBy,
  })

  // Save searchId to local storage
  setSearchId(query.data.searchId)

  return {
    ...query,
    data: { heroProducts: selectedData },
  }
}
